import {
    CLEAR_PROMOTED_SEARCH_RESULTS,
    LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
    LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
    LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
} from './actions';
import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getUserExcludedFacets } from './searchExclusions';
import { GlobalState } from '../rootReducer';
import api from '@/redux/api/search';

// reducer
export const DEFAULT_STATE = {
    all: [],
    itemIds: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let itemIDs;
    switch (action.type) {
        case CLEAR_PROMOTED_SEARCH_RESULTS:
        case LOAD_PROMOTED_SEARCH_RESULTS_FAIL:
            return DEFAULT_STATE;
        case LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS:
            itemIDs = action.payload.itemIDs || [];
            return {
                ...state,
                // @ts-ignore
                all: [...new Set([...state.all, ...itemIDs])], // prevent duplicates
                itemIds: itemIDs,
            };
        case LOAD_PROMOTED_SEARCH_RESULTS_REQUEST:
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchPromoted;

export const getPromotedSearchResults = createSelector(stateSelector, (state) => state.itemIds || []);

/* ACTION CREATORS */
export const clearPromotedSearchResults = () => ({
    payload: { itemIDs: [] },
    type: CLEAR_PROMOTED_SEARCH_RESULTS,
});

export const searchPromoted =
    ({ ids, pageName, searchQuery }: any) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const deployment = getDeployment(state);
            const authToken = getAuthToken(state);

            if (pageName === 'catalog' || pageName === 'price-guide' || pageName === 'sold-search') {
                return dispatch(clearPromotedSearchResults());
            }
            dispatch({
                payload: searchQuery,
                type: LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
            });

            // Annotate facets which have user-defined search exclusions
            const searchExclusions = getUserExcludedFacets(state);
            const augmentedOptions = !searchExclusions
                ? searchQuery.options
                : Object.keys(searchExclusions).reduce(
                      (options, facetWithExclusions) => ({
                          ...options,
                          [facetWithExclusions]: [
                              {
                                  exclude: searchExclusions[facetWithExclusions] || [],
                                  include: (searchQuery.options && searchQuery.options[facetWithExclusions]) || [],
                              },
                          ],
                      }),
                      { ...searchQuery.options }
                  );

            const augmentedSearchQuery = {
                ...searchQuery,
                options: augmentedOptions,
            };

            // for now, don't use user exclusions for promoted lots
            if (augmentedSearchQuery.options?.auctionHouse[0]) {
                augmentedSearchQuery.options.auctionHouse[0].exclude = [];
            }

            const results = await api.fetchPromotedSearchResults({
                authToken,
                deployment,
                ids,
                searchQuery: augmentedSearchQuery,
            });
            if (results.error) {
                dispatch({
                    error: true,
                    meta: { augmentedSearchQuery },
                    payload: results.payload,
                    type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
                });
                return dispatch(clearPromotedSearchResults());
            } else {
                return dispatch({
                    meta: { actionTime: Date.now() },
                    payload: results.payload,
                    type: LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
                });
            }
        } catch (error) {
            dispatch({
                error: true,
                meta: { searchQuery },
                payload: error,
                type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
            });
            return dispatch(clearPromotedSearchResults());
        }
    };
